import React, { useState } from 'react';
import { 
  FiUpload, FiDownload, FiDatabase, FiGlobe, FiZap, 
  FiSettings, FiActivity, FiFileText, FiUsers, FiBox
} from 'react-icons/fi';
import { MassImportDialog } from '../../components/import/MassImportDialog';
import { IntegrationManagement } from '../../components/integrations/IntegrationManagement';

interface ImportEntity {
  key: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  sampleCount: number;
}

export const ImportExportPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'import-export' | 'integrations'>('import-export');
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<string>('');

  const importEntities: ImportEntity[] = [
    {
      key: 'customers',
      label: 'Customers',
      description: 'Import customer contact information, billing details, and metadata',
      icon: FiUsers,
      sampleCount: 150
    },
    {
      key: 'products',
      label: 'Products',
      description: 'Import product catalog with pricing, inventory, and specifications',
      icon: FiBox,
      sampleCount: 500
    },
    {
      key: 'leads',
      label: 'Leads',
      description: 'Import sales leads with contact details and qualification data',
      icon: FiZap,
      sampleCount: 75
    },
    {
      key: 'employees',
      label: 'Employees',
      description: 'Import employee records with HR information and organizational data',
      icon: FiUsers,
      sampleCount: 25
    }
  ];

  const downloadTemplate = async (entity: string) => {
    try {
      // This would call the actual API endpoint
      const response = await fetch(`/api/v1/import/templates/${entity}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${entity}_import_template.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading template:', error);
      // For demo purposes, create a mock CSV
      const mockTemplates = {
        customers: 'name,email,phone,companyName,industry\nAcme Corp,contact@acme.com,+1-555-0123,Acme Corporation,Technology',
        products: 'itemCode,itemName,description,standardSellingRate,minStock\nPROD001,Sample Product,Sample description,100.00,10',
        leads: 'firstName,lastName,email,phone,companyName,source\nJohn,Smith,john@example.com,+1-555-0123,Example Corp,website',
        employees: 'employeeId,firstName,lastName,email,department,position,salary\nEMP001,Jane,Doe,jane@company.com,IT,Software Engineer,75000'
      };

      const csvContent = mockTemplates[entity as keyof typeof mockTemplates] || '';
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${entity}_import_template.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const startImport = (entityKey: string) => {
    setSelectedEntity(entityKey);
    setShowImportDialog(true);
  };

  const handleImportComplete = (result: any) => {
    console.log('Import completed:', result);
    // Show success message or refresh data
  };

  const ImportExportTab = () => (
    <div className="space-y-8">
      {/* Mass Import Section */}
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Mass Import</h2>
          <p className="text-gray-600">
            Import large datasets from CSV or Excel files. Download templates to ensure proper formatting.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {importEntities.map((entity) => {
            const Icon = entity.icon;
            return (
              <div key={entity.key} className="bg-white border rounded-lg p-6 hover:shadow-md transition-shadow">
                <div className="flex items-center space-x-4 mb-4">
                  <div className="p-3 bg-blue-100 rounded-lg">
                    <Icon size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg">{entity.label}</h3>
                    <p className="text-gray-600 text-sm">{entity.description}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    Template includes {entity.sampleCount}+ sample records
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => downloadTemplate(entity.key)}
                      className="flex items-center space-x-1 px-3 py-2 text-blue-600 border border-blue-600 rounded hover:bg-blue-50"
                    >
                      <FiDownload size={14} />
                      <span>Template</span>
                    </button>
                    <button
                      onClick={() => startImport(entity.key)}
                      className="flex items-center space-x-1 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                    >
                      <FiUpload size={14} />
                      <span>Import</span>
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Export Section */}
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Data Export</h2>
          <p className="text-gray-600">
            Export your data in various formats for backup, analysis, or migration purposes.
          </p>
        </div>

        <div className="bg-white border rounded-lg p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="p-4 bg-green-100 rounded-lg mx-auto w-fit mb-3">
                <FiDatabase size={32} className="text-green-600" />
              </div>
              <h3 className="font-semibold mb-2">Full Database Export</h3>
              <p className="text-gray-600 text-sm mb-4">
                Export all your data including customers, products, orders, and settings
              </p>
              <button className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700">
                Export All Data
              </button>
            </div>

            <div className="text-center">
              <div className="p-4 bg-orange-100 rounded-lg mx-auto w-fit mb-3">
                <FiFileText size={32} className="text-orange-600" />
              </div>
              <h3 className="font-semibold mb-2">Selective Export</h3>
              <p className="text-gray-600 text-sm mb-4">
                Choose specific data tables and date ranges for targeted exports
              </p>
              <button className="px-4 py-2 bg-orange-600 text-white rounded hover:bg-orange-700">
                Custom Export
              </button>
            </div>

            <div className="text-center">
              <div className="p-4 bg-purple-100 rounded-lg mx-auto w-fit mb-3">
                <FiActivity size={32} className="text-purple-600" />
              </div>
              <h3 className="font-semibold mb-2">Automated Backups</h3>
              <p className="text-gray-600 text-sm mb-4">
                Schedule regular automated backups to cloud storage or external systems
              </p>
              <button className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700">
                Setup Backup
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div>
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Recent Import/Export Activity</h2>
          <p className="text-gray-600">Track your recent data import and export operations.</p>
        </div>

        <div className="bg-white border rounded-lg">
          <div className="p-4 border-b">
            <h3 className="font-medium">Activity Log</h3>
          </div>
          <div className="divide-y">
            {[
              {
                id: 1,
                type: 'import',
                entity: 'Customers',
                records: 150,
                status: 'completed',
                user: 'John Doe',
                timestamp: '2 hours ago'
              },
              {
                id: 2,
                type: 'export',
                entity: 'Products',
                records: 500,
                status: 'completed',
                user: 'Jane Smith',
                timestamp: '1 day ago'
              },
              {
                id: 3,
                type: 'import',
                entity: 'Leads',
                records: 75,
                status: 'failed',
                user: 'Mike Johnson',
                timestamp: '2 days ago'
              }
            ].map((activity) => (
              <div key={activity.id} className="p-4 flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`p-2 rounded-lg ${
                    activity.type === 'import' 
                      ? 'bg-blue-100 text-blue-600' 
                      : 'bg-green-100 text-green-600'
                  }`}>
                    {activity.type === 'import' ? <FiUpload size={16} /> : <FiDownload size={16} />}
                  </div>
                  <div>
                    <div className="font-medium">
                      {activity.type === 'import' ? 'Imported' : 'Exported'} {activity.records} {activity.entity}
                    </div>
                    <div className="text-sm text-gray-600">
                      By {activity.user} • {activity.timestamp}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    activity.status === 'completed' 
                      ? 'bg-green-100 text-green-700'
                      : activity.status === 'failed'
                      ? 'bg-red-100 text-red-700'
                      : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {activity.status}
                  </span>
                  <button className="text-gray-400 hover:text-gray-600">
                    <FiDownload size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Import, Export & Integrations</h1>
          <p className="mt-2 text-gray-600">
            Manage your data flows, imports, exports, and external integrations.
          </p>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-8">
          <nav className="flex space-x-8">
            {[
              { key: 'import-export', label: 'Import & Export', icon: FiDatabase },
              { key: 'integrations', label: 'Integrations', icon: FiGlobe }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key as any)}
                  className={`flex items-center space-x-2 py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === tab.key
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon size={16} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'import-export' && <ImportExportTab />}
        {activeTab === 'integrations' && <IntegrationManagement />}

        {/* Mass Import Dialog */}
        <MassImportDialog
          isOpen={showImportDialog}
          onClose={() => setShowImportDialog(false)}
          entity={selectedEntity as any}
          onImportComplete={handleImportComplete}
        />
      </div>
    </div>
  );
}; 